import {
  CLUSTER_TYPE,
  DEFAULT_GLOBAL_SETTINGS,
  DETECTION_COLLECTION_KEYS,
  FILTER_PARAMETER_TYPE,
  FILTER_PARAMETER_TYPES,
  PLAYBACK_RATES,
  SEGMENT_SORT_BY_TYPE_ORDER,
  SEGMENT_TYPE,
} from '@helpers/constants';
import { isTextOnlyOverlayGroup } from '@helpers/textOverlay';
import { getOverlayStyles } from '@redact/components/MediaDetailsPage/Editor/MediaPlayer/getOverlayStyles';
import { selectFeatureFlags } from '@user-permissions';
import {
  NonEmptyArray,
  findClusterGroupIds,
  getDominantSegmentType,
} from '@utils';
import {
  BoundingPolyTree,
  collectGroupIdsInWindow,
  CollectionWrapper,
  GroupedBoundingPoly,
  search,
} from '@worker';
import { get, groupBy, isEmpty, omitBy, sortBy } from 'lodash';
import {
  createSelector,
  createSelectorCreator,
  lruMemoize as defaultMemoize,
} from 'reselect';
import {
  ClusterItem,
  ClusterItemGroup,
  ClusterSegment,
  TabState,
  ViewSettings,
} from '../models';
import { namespace } from '../store';
import { DetectionAssetState, Extra } from '../store.models';
import {
  selectCurrentPosition,
  selectDisableTranscriptionButton,
  selectPreviousPosition,
  selectSpeakerSeparationFetchSettled,
} from './general';
import {
  selectSpeakerSeparationAssetLoading,
  selectSpeakerSeparationCompleted,
  selectTranscriptionCompleted,
  selectTranscriptionFailed,
  selectTranscriptionRunning,
} from './tasks';
import {
  selectAudioRedactions,
  selectVoiceMaskEdits,
  selectTranscription,
} from './transcription';

export type MediaDetailsState = DetectionAssetState & ViewSettings & Extra;

const selectStore = (s: { [namespace]: MediaDetailsState }) => s[namespace];

export const selectMediaDuration = createSelector(selectStore, (s) =>
  ((tdo): number =>
    tdo
      ? get(tdo, 'primaryAsset.jsondata.mediaDuration') ||
        (new Date(tdo.stopDateTime).getTime() -
          new Date(tdo.startDateTime).getTime()) /
          1000
      : 0)(s.tdo)
);

export const selectGlobalSettings = createSelector(
  selectStore,
  (s) => s.tdo?.details?.settings || DEFAULT_GLOBAL_SETTINGS
);

export const selectFillBadgeEditAnchor = createSelector(
  selectStore,
  (s) => s.fillBadgeEditAnchor
);

export const selectShowGlobalSettingsModal = createSelector(
  selectStore,
  (s) => s.showGlobalSettingsModal
);

/**
 * VE-25876 — whether the Faces/Clusters list is scoped to the playhead. Held in
 * the store rather than the panel so it survives the right panel swapping to the
 * Audio/Comments tab and back; it resets with the rest of this slice on
 * ROUTE_MEDIA_DETAILS, i.e. when another TDO is opened.
 */
export const selectOnscreenOnlyFilter = createSelector(
  selectStore,
  (s) => s.onscreenOnlyFilter
);

/**
 * Whether the operator has made a choice about the playhead scope. Gates the
 * first-playback auto-engage; see `ViewSettings.onscreenOnlyFilterUserSet`.
 */
export const selectOnscreenOnlyFilterUserSet = createSelector(
  selectStore,
  (s) => s.onscreenOnlyFilterUserSet
);

export const selectExpandedPolyGroups = createSelector(
  selectStore,
  (s) => s.expandedPolyGroups
);

export const selectPolyGroups = createSelector(
  selectStore,
  (s) => s.selectedPolyGroups
);

/**
 * Interfaces.
 */

export const selectSortBy = createSelector(
  selectStore,
  (s) => s.sortPolyGroupsBy
);

export const selectShowNonRedactedOverlays = createSelector(
  selectGlobalSettings,
  (globalSettings) => globalSettings.showNonRedactedOverlays
);

export const selectFilterParameters = createSelector(
  selectStore,
  (s) => s.filterParameters
);

const selectDataFetchedFromStore = (s: { [namespace]: MediaDetailsState }) =>
  selectStore(s).dataFetchedForDetectionType;

export const selectDataFetchedForDetectionType = createSelector(
  selectDataFetchedFromStore,
  // selectFeatureFlags,
  (dataFetchedForDetectionType) => ({
    ...dataFetchedForDetectionType,
    notepad: Boolean(
      // dataFetchedForDetectionType?.notepad && featureFlags?.detectNotepads
      dataFetchedForDetectionType?.notepad // ignore flag VE-17384
    ),
    card: Boolean(
      // dataFetchedForDetectionType?.card && featureFlags?.detectCards
      dataFetchedForDetectionType?.card // ignore flag VE-17384
    ),
  })
);

export const selectHiddenObjectTypeCount = createSelector(
  selectFilterParameters,
  selectDataFetchedForDetectionType,
  (filterParameters, dataFetchedForDetectionType) => {
    let hiddenObjectTypeCount = 0;
    FILTER_PARAMETER_TYPES.forEach((type) => {
      switch (type) {
        case 'udr':
          hiddenObjectTypeCount += !filterParameters.show[type] ? 1 : 0;
          break;
        // case 'manual':
        //   filterCount +=
        //     (settings?.manualInterpolationMax ??
        //       DEFAULT_GLOBAL_SETTINGS.manualInterpolationMax) > 0 &&
        //     filterParameters.show[type]
        //       ? 1
        //       : 0;
        //   break;
        default: {
          hiddenObjectTypeCount +=
            !filterParameters.show[type] && dataFetchedForDetectionType[type]
              ? 1
              : 0;
        }
      }
    });
    hiddenObjectTypeCount +=
      filterParameters.show.textOverlay === false ? 1 : 0;
    return hiddenObjectTypeCount;
  }
);

export const selectAllSelectedFilter = createSelector(
  selectFilterParameters,
  selectDataFetchedForDetectionType,
  (filterParameters, dataFetchedForDetectionType) =>
    FILTER_PARAMETER_TYPES.every((type) => {
      // ignore if remove from output
      if (filterParameters.remove[type]) {
        return true;
      }

      switch (type) {
        case 'udr':
          return filterParameters.show[type] && !filterParameters.remove[type];
        // case 'manual':
        //   return (
        //     filterParameters.show[type] ||
        //     !(
        //       (settings?.manualInterpolationMax ??
        //         DEFAULT_GLOBAL_SETTINGS.manualInterpolationMax) > 0
        //     )
        //   );
        default: {
          return (
            filterParameters.show[type] || !dataFetchedForDetectionType[type]
          );
        }
      }
    }) &&
    (filterParameters.show.textOverlay !== false ||
      filterParameters.remove.textOverlay)
);

export const selectAllDisabledFilter = createSelector(
  selectFilterParameters,
  selectDataFetchedForDetectionType,
  (filterParameters, dataFetchedForDetectionType) =>
    FILTER_PARAMETER_TYPES.every((type) => {
      switch (type) {
        case 'udr':
          return filterParameters.remove[type];
        // case 'manual':
        //   return (
        //     filterParameters.remove[type] ||
        //     !(
        //       (settings?.manualInterpolationMax ??
        //         DEFAULT_GLOBAL_SETTINGS.manualInterpolationMax) > 0
        //     )
        //   );
        default: {
          return (
            filterParameters.remove[type] || !dataFetchedForDetectionType[type]
          );
        }
      }
    }) && filterParameters.remove.textOverlay
);

export const selectOverlayPreview = createSelector(
  selectStore,
  (s) => s.overlayPreview
);

export const selectOverlayTool = createSelector(
  selectStore,
  (s) => s.overlayTool
);

export const selectOverlayStyles = createSelector(
  selectOverlayPreview,
  (overlayPreview) => getOverlayStyles(overlayPreview)
);

export const selectAudioPreview = createSelector(
  selectStore,
  (s) => s.audioPreview
);

export const selectIsPlayerSeeked = createSelector(
  selectStore,
  (s) => s.isPlayerSeeked
);

export const selectPlayback = createSelector(
  selectStore,
  ({ playbackDirection, playbackSpeed }) => ({
    playbackDirection,
    playbackSpeed,
  })
);

export const selectLastSaveMediaDataDisplayTime = createSelector(
  selectStore,
  (s) => s.lastSaveMediaDataDisplayTime
);

export const selectHasSavedUdrs = createSelector(
  selectStore,
  (s) => s.hasSavedUdrs
);

export const selectHasSavedDetectionss = createSelector(
  selectStore,
  (s) => s.hasSavedDetections
);

export const selectDetectionClusterGroups = createSelector(
  selectStore,
  (s) => s.detectionClusterGroups
);

export const selectDetectionClusterGroupsLoaded = createSelector(
  selectStore,
  (s) => s.detectionClusterGroupsLoaded
);

export const selectDetectionClusterGroupsCount = createSelector(
  selectDetectionClusterGroups,
  (groups) => (groups ? Object.keys(groups).length : undefined)
);

export const selectUdrClusterGroups = createSelector(
  selectStore,
  (s) => s.udrClusterGroups
);
export const selectUdrClusterGroupsLoaded = createSelector(
  selectStore,
  (s) => s.udrClusterGroupsLoaded
);

export const selectUdrClusterGroupsCount = createSelector(
  selectUdrClusterGroups,
  (groups) => (groups ? Object.keys(groups).length : undefined)
);

export const selectClusterMap = createSelector(
  selectStore,
  (s) => s.clusterMap
);

/**
 * Select cluster list for right-side panel.
 */
export const selectClusterList = createSelector(
  selectDetectionClusterGroups,
  selectUdrClusterGroups,
  selectClusterMap,
  (detectionClusterGroups, udrClusterGroups, clusterMap) => {
    const groupsByClusterId: { [clusterId: string]: ClusterItemGroup[] } = {};
    // add detection groups
    if (detectionClusterGroups) {
      for (const group of Object.values(detectionClusterGroups)) {
        // filtering now done in clusters.ts
        // if (!featureFlags.detectCards && group.type === 'card') {
        //   continue;
        // }

        // if (!featureFlags.detectNotepads && group.type === 'notepad') {
        //   continue;
        // }

        const groups = groupsByClusterId[group.clusterId];
        if (groups) {
          groups.push(group);
        } else {
          groupsByClusterId[group.clusterId] = [group];
        }
      }
    }

    // add udr groups
    if (udrClusterGroups) {
      for (const group of Object.values(udrClusterGroups)) {
        const groups = groupsByClusterId[group.clusterId];
        if (groups) {
          groups.push(group);
        } else {
          groupsByClusterId[group.clusterId] = [group];
        }
      }
    }

    // final packaging of clusterItems
    const clusters: ClusterItem[] = [];
    for (const [clusterId, groupsUnsorted] of Object.entries(
      groupsByClusterId
    )) {
      // determine cluster type

      // sort groups in each cluster by start time of first segment
      // const groups = sortBy(groupsUnsorted, 'startTimeMs');
      const groups = groupsUnsorted.sort(
        (a, b) => a.segments[0].startTimeMs - b.segments[0].startTimeMs
      );

      let firstGroup = groups[0];
      if (!firstGroup) {
        continue;
      }

      // concatenate all segments
      let segments: ClusterSegment[] = [];
      for (const group of groups) {
        segments.push(...group.segments);
      }

      let type: CLUSTER_TYPE = firstGroup.type;
      let dominantType: SEGMENT_TYPE | undefined;
      if (groups.length > 1) {
        // check if there is more than more type
        const uniqueTypes = Array.from(
          new Set(groups.map((group) => group.type))
        );
        if (uniqueTypes.length > 1) {
          type = 'mixed';

          // determine most common segment type
          dominantType = getDominantSegmentType(segments.map((v) => v.type));

          // update default group for pic thumbnail to use first group with dominant type
          const firstGroupDominantType = groups.find(
            (group) => group.type === dominantType
          );
          if (firstGroupDominantType) {
            firstGroup = firstGroupDominantType;
          }
        }
      }

      let userLabel;

      // if single group cluster use group label
      if (groups.length === 1) {
        userLabel = firstGroup.userLabel;
      }

      let pinnedDate = undefined;
      if (clusterMap[clusterId]) {
        userLabel = clusterMap[clusterId].userLabel; // clusterMap userLabel will take priority over group userLabel
        pinnedDate = clusterMap[clusterId].pinnedDate;

        // sort segments
        if (clusterMap[clusterId].sortBy === 'type') {
          const segmentGroupedByType = groupBy(segments, 'type');

          const sortedSegments: ClusterSegment[] = [];
          SEGMENT_SORT_BY_TYPE_ORDER.forEach((segType) => {
            if (segmentGroupedByType[segType]) {
              sortedSegments.push(
                ...sortBy(segmentGroupedByType[segType], 'startTimeMs')
              );
            }
          });

          segments = sortedSegments;
        } else if (clusterMap[clusterId].sortBy === 'group') {
          // already sorted by group
        } else {
          // default to sortBy time
          segments = sortBy(segments, 'startTimeMs');
        }
      }

      const startTimeMs = Math.min.apply(
        null,
        segments.map((segment) => segment.startTimeMs)
      );

      const stopTimeMs = Math.max.apply(
        null,
        segments.map((segment) => segment.stopTimeMs)
      );

      const group = groups[0];
      const isTextOverlay =
        groups.length === 1 && !!group && isTextOnlyOverlayGroup(group);

      const clusterItem: ClusterItem = {
        id: clusterId,
        userLabel,
        pinnedDate,
        picUri: firstGroup.picUri,
        groups: groups as NonEmptyArray<ClusterItemGroup>,
        segments: segments as NonEmptyArray<ClusterSegment>,
        type,
        ...(dominantType && { dominantType }),
        segmentSortType: clusterMap[clusterId]?.sortBy ?? 'time', // Default, sort by time in ascending order
        startTimeMs,
        stopTimeMs,
        isMergedGroup: groups.length > 1,
        isTextOverlay,
        // version: firstGroup.version, // may not need this
      };
      clusters.push(clusterItem);
    }

    return clusters;
  }
);

export const selectSelected = createSelector(
  selectStore,
  selectClusterList,
  selectFeatureFlags,
  (s, clusterList, { analyzeInterpolation }) =>
    analyzeInterpolation
      ? clusterList.reduce<Record<string, boolean>>(
          (listPrev, cluster) => ({
            ...listPrev,
            ...cluster.groups.reduce<Record<string, boolean>>(
              (groupPrev, group) => ({
                ...groupPrev,
                ...group.segments.reduce<Record<string, boolean>>(
                  (segmentPrev, segment) => ({
                    ...segmentPrev,
                    [segment.id]: segment.isManualInterpolation,
                  }),
                  {}
                ),
              }),
              {}
            ),
          }),
          {}
        )
      : s.selectedPolyGroups
);

export const selectClusterMergeGroupIds = createSelector(
  selectStore,
  (s) => s.clusterMergeGroupIds
);

export const selectClusterMergeSegments = createSelector(
  selectStore,
  (s) => s.clusterMergeSegments
);

export const selectFilterParametersFromStore = (s: {
  [namespace]: MediaDetailsState;
}) => selectStore(s).filterParameters;

export const selectUDRCollection = createSelector(
  selectStore,
  selectFilterParametersFromStore,
  (s, filterParameters) => {
    if (filterParameters.show.udr) {
      return s.udrCollection;
    }
    return undefined;
  }
);

export const selectTextOverlayCollection = createSelector(
  selectStore,
  selectFilterParametersFromStore,
  (s, filterParameters) =>
    filterParameters.show.textOverlay === false
      ? undefined
      : s.textOverlayCollection
);

const createVersionSelector = createSelectorCreator(
  defaultMemoize,
  (
    a: { [key: string]: CollectionWrapper | undefined },
    b: { [key: string]: CollectionWrapper | undefined }
  ) => {
    // list of union of keys - probably a more elegant way to do this
    let allKeys: string[] = [];
    if (a) {
      allKeys = allKeys.concat(Object.keys(a));
    }
    if (b) {
      allKeys = allKeys.concat(Object.keys(b));
    }
    allKeys = [...new Set(allKeys)];

    // check that all collection versions still match
    const allMatch =
      (isEmpty(a) && isEmpty(b)) ||
      allKeys.every(
        (key) =>
          a?.[key] &&
          b?.[key] &&
          a?.[key]?.collection.version === b?.[key]?.collection.version
      );
    return allMatch;
  }
);

export const selectDetectionCollections = createVersionSelector(
  createSelector(
    selectStore,
    selectFilterParameters,
    // selectFeatureFlags,
    (s, filterParameters) => ({
      ...omitBy(
        s.detectionCollections,
        (_value, key) =>
          // (!featureFlags.detectCards && key.includes('card')) || // if has data ignore flag VE-17384
          // (!featureFlags.detectNotepads && key.includes('notepad')) || // if has data ignore flag VE-17384
          !filterParameters.show[key as FILTER_PARAMETER_TYPE] // FTODO
      ),
    })
  ),
  (detectionCollections) => detectionCollections
);

export const selectProgress = createSelector(selectStore, (s) => s.progress);

export const isFetchingFaceDetectionSelector = createSelector(
  selectStore,
  (s) => s.faceDetection.isFetching
);

export const isFaceDetectionProcessingSelector = createSelector(
  selectStore,
  (s) => s.faceDetection.isProcessing
);

export const faceDetectionInfoSelector = createSelector(
  selectStore,
  (s) => s.faceDetection
);

export const isFaceDetectionReadySelector = createSelector(
  selectStore,
  (s) => s.faceDetection.isReady
);

export const isRedactionMediaDataReadySelector = createSelector(
  selectStore,
  (s) => s.redactionMediaDataReady
);

export const mediaHasAudioSelector = createSelector(
  selectStore,
  (s) => s && s.hasAudio
);

/**
 * Select highlighted Head
 */
export const selectHighlightedOverlay = createSelector(
  selectStore,
  (s) => s.highlightedOverlay
);

/**
 * Select highlighted Head id
 */
export const selectHighlightFaceId = createSelector(
  selectHighlightedOverlay,
  (f) => get(f, 'id')
);

export const selectUDRBoundingPolyBeingUpdated = createSelector(
  selectStore,
  (s) => s.udrsState.udrBoundingPolyBeingUpdated
);

/**
 * Visit the three collections that make up "what the player draws" — the
 * type-filtered detection collections, the UDRs and the text overlays — in that
 * order, skipping any that are absent.
 *
 * One walk, so the three selectors below cannot drift apart on which sources
 * count or in what order. `search` and `collectGroupIdsInWindow` both tolerate
 * an undefined collection, so callers need no guard of their own.
 */
const forEachPlayheadCollection = (
  detectionCollections:
    | { [key: string]: CollectionWrapper | undefined }
    | undefined,
  udrCollection: CollectionWrapper | undefined,
  textOverlayCollection: CollectionWrapper | undefined,
  visit: (collection: BoundingPolyTree | undefined) => void
): void => {
  if (detectionCollections) {
    for (const key of DETECTION_COLLECTION_KEYS) {
      visit(detectionCollections[key]?.collection);
    }
  }
  visit(udrCollection?.collection);
  visit(textOverlayCollection?.collection);
};

export const selectPolysAtCurrentTime = createSelector(
  selectDetectionCollections,
  selectUDRCollection,
  selectTextOverlayCollection,
  selectCurrentPosition,
  (detectionCollections, udrCollection, textOverlayCollection, time) => {
    const result: GroupedBoundingPoly[] = [];

    forEachPlayheadCollection(
      detectionCollections,
      udrCollection,
      textOverlayCollection,
      (collection) => {
        for (const poly of search(collection, time)) {
          result.push(poly);
        }
      }
    );

    return result;
  }
);

/**
 * Whether there is anything for `selectPlayheadActiveGroupIds` to search yet.
 *
 * That selector returns an empty array both when nothing is on screen and when
 * the worker has not published the interval trees — the cluster list arrives on a
 * different payload, so the two are briefly out of step. The states look
 * identical from the result alone, and they mean opposite things: one supports
 * telling the operator the frame is clear, the other supports saying nothing at
 * all (VE-25876).
 *
 * Type-filtered like its inputs, so it also reads false when every type is
 * hidden. Callers reach that case through `selectHiddenObjectTypeCount` first, so
 * the overlap is harmless.
 */
export const selectPlayheadCollectionsPresent = createSelector(
  selectDetectionCollections,
  selectUDRCollection,
  selectTextOverlayCollection,
  (detectionCollections, udrCollection, textOverlayCollection) => {
    let present = false;

    forEachPlayheadCollection(
      detectionCollections,
      udrCollection,
      textOverlayCollection,
      (collection) => {
        present = present || !!collection;
      }
    );

    return present;
  }
);

/**
 * Backstop only — NOT the seek discriminator. That job belongs to
 * `playheadJumped`, which the reducer sets from the actual seek actions, because
 * a seek and a playback step differ in kind rather than in size: 1.5s is both a
 * short seek and one sample at 8x playback.
 *
 * This bound exists solely to contain a seek that somehow reached us unflagged,
 * so it must be wider than any genuine playback step can ever be. Firing on a
 * real step would collapse the window to a point and reinstate the
 * dropped-segment bug this whole mechanism exists to prevent — silently, and at
 * the fastest playback speed, where a short detection is hardest to catch by eye.
 *
 * The widest genuine step is one `timeupdate` interval at the fastest offered
 * rate. `timeupdate` is UA best-effort (nominally ~250ms) and this app shares the
 * main thread with a second Redux store, a Konva timeline and pdf/mupdf workers,
 * so it stretches under load; this allows a badly stalled 1.25s tick at the top
 * rate. Erring wide costs some over-reporting, the safe direction here; erring
 * narrow hides objects from the operator.
 */
const STALLED_SAMPLE_INTERVAL_MS = 1250;
const SAMPLE_WINDOW_BACKSTOP_MS =
  // Math.max rather than the last element: indexing the tail silently narrows
  // the backstop if PLAYBACK_RATES is ever reordered or appended to, and a
  // backstop that is too narrow fires on genuine playback steps — the one
  // failure mode this constant exists to avoid.
  Math.max(...PLAYBACK_RATES) * STALLED_SAMPLE_INTERVAL_MS;

/**
 * Group ids of every detection/UDR/text-overlay that was on screen at any point
 * between the previous playhead sample and the current one (VE-24264, VE-25876).
 *
 * A window, not an instant, and deliberately so. `currentPosition` is fed by the
 * DOM `timeupdate` event — roughly 4Hz, see `MediaPlayerView` — while the overlay
 * boxes are drawn from the video's own frame callback in `useCollection`. A point
 * query here would therefore sample the timeline every ~250ms and drop any
 * segment narrower than that, never reporting it at all. Omitting an object from
 * "what is on screen" is the failure that matters in a redaction tool;
 * over-reporting by a sample gap is not.
 *
 * The window is therefore centred on the painted frame rather than trailing it:
 * it opens at the previous sample and closes one gap PAST the latest one, since
 * the video has already advanced beyond `currentPosition` by the time anything
 * reads this. Both edges over-reach by up to a gap, deliberately — a card may
 * appear slightly early or linger slightly late, and neither misleads the
 * operator the way a missing card does.
 *
 * Derived from the *type-filtered* collections (`selectDetectionCollections` and
 * friends apply `filterParameters.show`), so an empty result does NOT prove the
 * frame is clear — it equally means the only thing on screen is a hidden type.
 * Anything that tells the user the frame is clear must consult
 * `selectHiddenObjectTypeCount` too; see `OnscreenEmptyState`.
 *
 * Returns a sorted array so consumers can gate re-renders with `shallowEqual` —
 * the value then only changes when the on-screen set actually changes, not on
 * every sample. Subscribing without that equality function puts the subscriber
 * on the per-sample render path.
 */
export const selectPlayheadActiveGroupIds = createSelector(
  selectDetectionCollections,
  selectUDRCollection,
  selectTextOverlayCollection,
  selectCurrentPosition,
  selectPreviousPosition,
  (
    detectionCollections,
    udrCollection,
    textOverlayCollection,
    position,
    previous
  ) => {
    // `previous` has already been collapsed onto `position` by the reducer when
    // the playhead was seeked, so a jump normally arrives as delta 0 and the
    // bound below is only a backstop.
    //
    // The `delta >= 0` guard treats every backward step as a jump, which is
    // right for a seek and wrong for reverse playback — that is a continuous
    // mode, and under it every sample would be negative and the window would
    // stay collapsed for its whole duration. Dormant: `actionPlayVideoBackwards`
    // is defined but nothing dispatches it. Whoever wires it up wants
    // `Math.abs(delta) <= SAMPLE_WINDOW_BACKSTOP_MS` here;
    // `collectGroupIdsInWindow` is already direction-agnostic.
    const delta = position - previous;
    const isPlaybackStep = delta >= 0 && delta <= SAMPLE_WINDOW_BACKSTOP_MS;

    // Both endpoints come from `timeupdate`, so `position` is the LAST sample —
    // not the frame currently painted, which the video has already advanced past
    // by up to one more gap. Bounding the window at `position` therefore leaves
    // the leading edge uncovered: an object entering after the last sample is
    // drawn on the video and missing from this set, and the panel would report a
    // clear frame over a visible face. Extrapolate one gap forward so the window
    // brackets the painted frame instead of trailing it. Over-reaching ahead
    // costs a card that lingers slightly early or late; under-reaching hides an
    // object from the operator, which in a redaction tool is not a symmetric
    // trade.
    const from = isPlaybackStep ? previous : position;
    const to = isPlaybackStep ? position + delta : position;

    const ids = new Set<string>();

    forEachPlayheadCollection(
      detectionCollections,
      udrCollection,
      textOverlayCollection,
      (collection) => collectGroupIdsInWindow(collection, from, to, ids)
    );

    return [...ids].sort();
  }
);

export const selectHighlightedPoly = createSelector(
  selectHighlightedOverlay,
  selectPolysAtCurrentTime,
  (highlightedOverlay, polys) => {
    if (!highlightedOverlay) {
      return null;
    }
    // search by id first
    let match = polys.find((p) => p.id === highlightedOverlay.id);

    // if no match search for matching group
    if (!match) {
      match = polys.find(
        (p) => !!p.groupId && p.groupId === highlightedOverlay.groupId
      );
    }
    return match;
  }
);

export const selectHighlightedClusterItem = createSelector(
  selectClusterList,
  selectHighlightedOverlay,
  selectUdrClusterGroups,
  selectDetectionClusterGroups,
  (
    clusterList,
    highlightedOverlay,
    udrClusterGroups,
    detectionClusterGroups
  ) => {
    let clusterId: string | undefined;

    if (highlightedOverlay) {
      if (highlightedOverlay.type === 'udr') {
        clusterId = udrClusterGroups[highlightedOverlay.groupId]?.clusterId;
      } else {
        clusterId =
          detectionClusterGroups[highlightedOverlay.groupId]?.clusterId;
      }
    }

    if (clusterId) {
      return clusterList.find((cl) => cl.id === clusterId);
    }
  }
);

export const selectHighlightedClusterItemGroup = createSelector(
  selectHighlightedOverlay,
  selectUdrClusterGroups,
  selectDetectionClusterGroups,
  (highlightedOverlay, udrClusterGroups, detectionClusterGroups) => {
    if (highlightedOverlay) {
      if (highlightedOverlay.type === 'udr') {
        return udrClusterGroups[highlightedOverlay.groupId];
      } else {
        return detectionClusterGroups[highlightedOverlay.groupId];
      }
    }
  }
);

export const selectBackgroundBlurEdits = createSelector(
  selectStore,
  (s) => s.backgroundBlurEdits
);

export const selectHasAnythingToRedact = createSelector(
  selectClusterList,
  selectSelected,
  selectAudioRedactions,
  selectVoiceMaskEdits,
  selectBackgroundBlurEdits,
  (list, selected, audioRedactionEdits, voiceMaskEdits, backgroundBlurEdits) =>
    audioRedactionEdits.length > 0 ||
    voiceMaskEdits.length > 0 ||
    backgroundBlurEdits.length > 0 ||
    list.reduce<boolean>(
      (b, l) =>
        b ||
        l.groups.some((g) =>
          g.segments.some((s) =>
            s.subsegmentIds.some((ssid) => !!selected[ssid])
          )
        ), // CTODO this could be simplified is went back to including groups in the selected - not sure worth it
      false
    )
);

export const selectorVideoLoaded = createSelector(
  selectStore,
  (s) => s && s.videoLoaded
);

export const selectThumbnailTracks = createSelector(
  selectStore,
  (s) => s && s.thumbnailTracks
);

export const selectShakaPlayer = createSelector(
  selectStore,
  (s) => s && s.shakaPlayer
);

/**
 * Select the state of the AUDIO tab.
 * ENGINE_RUNNING, NOT_RUN, LOADING, EMPTY, COMPLETE, FAILED
 *
 * The order of the questions matters, and it is not the order it reads in
 * (VE-31771). The first one is whether there is a TRANSCRIPT TO SHOW, because
 * that decides which half of the tab we are in:
 *
 *   - There is one. A run on top of it is a re-run, and ENGINE_RUNNING hides
 *     the old transcript behind a spinner while it is replaced — unchanged.
 *   - There is not. Then the tab shows its panel, and the panel reports which
 *     engines are running ROW BY ROW rather than being replaced wholesale by
 *     one spinner titled "Transcribing Audio…" — which was the same spinner
 *     whether transcription or speaker separation was running, and named only
 *     the first.
 *
 * ENGINE_RUNNING is no longer returned at all. It existed to hide the tab
 * behind one spinner while anything ran, which meant a SEPARATION run — which
 * leaves the transcript perfectly readable — replaced it with a spinner
 * mislabelled "Transcribing Audio…". Only transcription replaces the text, so
 * only transcription is a reason to stop showing it; everything else is
 * reported by the rows or by the toolbar, over the transcript rather than
 * instead of it.
 *
 * NOT_RUN is therefore reached with engines in flight, and with a transcript
 * that is about to be replaced. It is named for there being nothing to SHOW —
 * not for idleness, and not for absence.
 */
export const selectAudioTabState = createSelector(
  selectTranscription,
  selectProgress,
  selectTranscriptionRunning,
  selectDisableTranscriptionButton,
  mediaHasAudioSelector,
  selectTranscriptionCompleted,
  selectTranscriptionFailed,
  // Separation counts as an engine running on this tab. It is the same signal
  // the toolbar's "Separating speakers" indicator uses, so the two cannot
  // disagree — without it the tab offered "No engines have been run" while the
  // toolbar said separation was in progress.
  selectSpeakerSeparationAssetLoading,
  // The two inputs behind the layout gate below.
  selectSpeakerSeparationCompleted,
  selectSpeakerSeparationFetchSettled,
  (
    t,
    p,
    r,
    isDisabled,
    hasAudio,
    completedTasks,
    failedTasks,
    separationRunning,
    separationCompleted,
    separationFetchSettled
  ) => {
    // `isDisabled` is really "a transcription is in progress" — it also covers
    // the window between a run reporting success and its transcript arriving,
    // which the tasks no longer cover (VE-30784).
    // Transcription specifically, not "any engine": it is the only one that
    // REPLACES the transcript, so it is the only one whose run is a reason to
    // stop showing the current text.
    const transcriptionRunning = r.length > 0 || isDisabled;
    const anythingRunning = transcriptionRunning || separationRunning;

    // Asked BEFORE "is there a transcript", because a run that completed and
    // produced no words is an answer — silent audio — not a transcript to
    // show and not an absence to offer a Run button for.
    if (!hasAudio || (t?.transcription.length === 0 && completedTasks.length)) {
      return TabState.EMPTY;
    }

    // When (t.transcription.length === 0), t.startOffsetMs === t.stopOffsetMs
    // is also true, which is why a 0/0 transcript reads as nothing to show.
    const hasTranscriptToShow = !!t && t.startOffsetMs !== t.stopOffsetMs;

    // Speaker separation and voice mask leave the transcript where it is —
    // separation regroups it, voice mask does not touch it — so neither is a
    // reason to take it off screen. The toolbar's own indicator reports them.
    //
    // This is what retired ENGINE_RUNNING: it hid a perfectly readable
    // transcript behind a full-panel spinner titled "Transcribing Audio…" for
    // the length of a SEPARATION run, which was both a lie and a loss.
    if (hasTranscriptToShow) {
      if (transcriptionRunning) {
        return TabState.NOT_RUN;
      }
      // The transcript is ready, but the SHAPE of the panel is not decided
      // yet. Speaker segments arrive on their own fetch, racing the
      // transcript's; rendering on the transcript alone commits to the flat
      // layout and then re-lays the panel out under the reader when segments
      // land. Hold the spinner for that window.
      //
      // Narrow on purpose. It only ever turns COMPLETE into LOADING — never
      // NOT_RUN, FAILED or EMPTY, each of which is an answer the user can act
      // on, and showing a spinner over them is precisely the regression
      // VE-30784 was about. And it only waits when a separation run has
      // COMPLETED, so a TDO that never ran it (the common case) is unaffected
      // whatever the feature flag says.
      //
      // Cannot hang: the settled signal is published either when the fetch
      // answers or when the worker decides not to issue one at all.
      if (separationCompleted && !separationFetchSettled) {
        return TabState.LOADING;
      }
      return TabState.COMPLETE;
    }

    // Work in flight beats both of the answers below, because both of them
    // describe a PREVIOUS run and neither is superseded when a new one starts
    // (`selectTranscriptionFailed` has no supersession filter, and a failed
    // fetch deliberately leaves `progress` where it was).
    //
    // Without this, retrying from the FAILED panel left the error image and
    // its copy on screen for the whole run — the retry button greys out and
    // nothing else changes, because FAILED was still true and nothing above
    // it was. The panel reports the run row by row instead.
    if (anythingRunning) {
      return TabState.NOT_RUN;
    }

    // Asked BEFORE loading, because a failed run is an answer and loading is
    // the absence of one. The transcript fetch after a failed run fails too,
    // and `OUT_TRANSCRIPTION_FETCH_FAILED` leaves `progress.transcription` at
    // 0 on purpose — so asking loading first returned LOADING with nothing
    // left in the system able to ever set progress to 1. A permanent spinner
    // where the user should get the error and its retry.
    //
    // Making an assumption that once transcription is complete cannot re-run
    // transcription and the latest would be failed.
    if (failedTasks.length && !completedTasks.length) {
      return TabState.FAILED;
    }

    // Before anything has been looked for, say nothing. Absence is only
    // reported once the fetch has answered.
    if (t === undefined && p.transcription !== 1) {
      return TabState.LOADING;
    }

    return TabState.NOT_RUN;
  }
);

/**
 * Whether audio transcription work has HAPPENED on this TDO — results, or a
 * run in flight. A domain fact, not a view state.
 *
 * Callers outside the audio tab used to ask `selectAudioTabState !== NOT_RUN`
 * for this. That worked only while NOT_RUN meant "nothing has been run";
 * VE-31771 widened it to "no transcript to show", which includes a run in
 * progress, and the borrowed question quietly started answering `false`
 * mid-run. The trim flow used it to decide whether a clipped TDO carries
 * transcription results, so a trim taken during a run created a TDO claiming
 * it had none.
 *
 * Same shape as the head-detection check beside it (`list.length ||
 * runningTasks.length`): results or work, not a screen state.
 */
export const selectAudioTranscriptionStarted = createSelector(
  selectAudioTabState,
  selectTranscriptionRunning,
  selectDisableTranscriptionButton,
  selectSpeakerSeparationAssetLoading,
  (tabState, running, isDisabled, separationRunning) =>
    tabState !== TabState.NOT_RUN ||
    running.length > 0 ||
    isDisabled ||
    !!separationRunning
);

export const selectRegularModePlaybackSpeed = createSelector(
  selectStore,
  (s) => s.udrsState.regularModePlaybackSpeed
);

export const selectAudiowaves = createSelector(
  selectStore,
  (s) => s.audiowaves
);

export const selectCaseMedia = createSelector(selectStore, (s) => s.caseMedia);

export const selectIsCaseMediaDrawerOpen = createSelector(
  selectStore,
  (s) => s.isCaseMediaDrawerOpen
);

export const selectMediaComments = createSelector(
  selectStore,
  (s) => s.mediaComments
);

export const selectMediaCommentDrafts = createSelector(
  selectStore,
  (s) => s.mediaCommentDrafts ?? {}
);

export const selectEditingMediaCommentId = createSelector(
  selectStore,
  (s) => s.editingMediaCommentId
);

export const selectIsFetchingComments = createSelector(
  selectStore,
  (s) => s.isFetchingComments
);

export const selectIsHideComment = createSelector(
  selectStore,
  (s) => s.isHideComment
);

export const selectCommentUsers = createSelector(
  selectStore,
  (s) => s.mediaCommentsUsers
);

export const selectUser = createSelector(selectStore, (s) => s.user);

export const selectSelectedComments = createSelector(
  selectStore,
  (s) => s.selectedMediaComments
);

export const selectIsBasicUserQuerySupported = createSelector(
  selectStore,
  (s) => s.isBasicUserQuerySupported
);

export const selectTrimInterval = createSelector(
  selectStore,
  (s) => s.tdo?.details?.redact?.trimInterval
);

export const selectTdoLockInfo = createSelector(
  selectStore,
  (s) => s.tdoLockInfo
);

export const selectExternalRedactionStatus = createSelector(
  selectStore,
  (s) => s.externalRedactionStatus
);

export const selectMergedSetBlock = createSelector(
  selectStore,
  (s) => s.mergedSetBlock
);

export const selectPrevMarkerTime = createSelector(
  selectStore,
  (s) => s.prevMarkerTime
);

export const selectDraggedOverlayId = createSelector(
  selectStore,
  (s) => s.draggedOverlayId
);

export const selectIsSaveFailed = createSelector(
  selectStore,
  (s) => s.isSaveFailed
);

export const selectClusterGroupIds = createSelector(
  selectClusterMap,
  selectUdrClusterGroups,
  selectDetectionClusterGroups,
  selectHighlightedOverlay,
  (clusterMap, udrClusterGroups, detectionClusterGroups, highlightedOverlay) =>
    findClusterGroupIds(
      clusterMap,
      udrClusterGroups,
      detectionClusterGroups,
      highlightedOverlay?.groupId
    )
);

export const selectTdoHasPermissions = createSelector(
  selectStore,
  (s) => s.tdoHasPermissions
);
